import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { http } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Monitor, Save, X, Cpu, HardDrive, User, MapPin, FileText, ChevronRight, ChevronDown, Loader2, Wifi, Printer, BatteryCharging, Network, Layers } from 'lucide-react';
import SearchableSelect from "../ui/SearchableSelect";

const TIPOS_EQUIPO = [
  "AP", "DVR", "Firewall", "Impresora", "MiniPC", "NAS", "Notebook", "NVR", "PC", "Router", "Servidor", "Switch", "Tablet", "UPS"
];

function getCategoriaEquipo(tipo) {
  if (['PC', 'Notebook', 'MiniPC', 'Servidor', 'Tablet', 'NAS'].includes(tipo)) return 'computador';
  if (['Router', 'AP', 'Switch', 'Firewall'].includes(tipo)) return 'red';
  if (tipo === 'Impresora') return 'impresora';
  if (tipo === 'UPS') return 'ups';
  return 'generico';
}

export default function EquipoFormMobile({ equipo = null, onGuardado, onCancelar }) {
  const { user } = useAuth();
  const [clientes, setClientes] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [departamentosDisponibles, setDepartamentosDisponibles] = useState([]);
  const [guardando, setGuardando] = useState(false);
  
  // Estados para secciones colapsables en móvil
  const [seccionAbierta, setSeccionAbierta] = useState('basicos');
  
  const secciones = {
    basicos: "Información Básica",
    especificaciones: "Especificaciones",
    cliente: "Cliente y Ubicación",
    observaciones: "Observaciones"
  };

  const [form, setForm] = useState({
    nombre: "",
    marca: "",
    modelo: "",
    numero_serie: "",
    tipo_equipo: "",
    procesador: "",
    memoria_ram: "",
    tipo_ram: "",
    disco: "",
    tipo_disco: "",
    tipo_memoria: "",
    sistema_operativo: "",
    pantalla_pulgadas: "",
    conectividad: "",
    puertos_lan: "",
    protocolo_wifi: "",
    frecuencia_wifi: "",
    velocidad_wan: "",
    tipo_switch: "",
    poe: "",
    firmware_version: "",
    tipo_impresion: "",
    conexion_impresora: "",
    resolucion_dpi: "",
    formato_papel: "",
    capacidad_va: "",
    potencia_w: "",
    autonomia_min: "",
    tipo_bateria_ups: "",
    estado: "operativo",
    ubicacion: "",
    observaciones: "",
    cliente_id: "",
    sucursal: ""
  });

  useEffect(() => {
    cargarClientes();
  }, []);

  useEffect(() => {
    if (equipo) {
      console.log("Equipo recibido en formulario:", equipo);
      
      // Extraer cliente_id de múltiples posibles formatos
      const clienteIdRaw = equipo.cliente_id || 
                           equipo.clienteId || 
                           equipo.cliente?.id || 
                           equipo.cliente?._id ||
                           equipo.cliente_lookup_ids?.[0] ||
                           "";
      
      console.log("Cliente ID extraído (raw):", clienteIdRaw);
      
      // Buscar el cliente en la lista para normalizar el ID
      let clienteEncontrado = null;
      let clienteIdNormalizado = clienteIdRaw;
      
      if (clienteIdRaw && clientes.length > 0) {
        clienteEncontrado = clientes.find(c => 
          c.id === clienteIdRaw || 
          c._id === clienteIdRaw || 
          c.primaryId === clienteIdRaw ||
          (c.lookupIds && c.lookupIds.includes(clienteIdRaw))
        );
        
        // Si encontramos el cliente, usar su primaryId para que coincida con el select
        if (clienteEncontrado) {
          clienteIdNormalizado = clienteEncontrado.primaryId || clienteEncontrado.id;
          console.log("Cliente encontrado - normalizado a primaryId:", clienteIdNormalizado);
        } else {
          console.warn("Cliente NO encontrado en la lista para ID:", clienteIdRaw);
        }
      }
      
      setForm({
        nombre: equipo.nombre || "",
        marca: equipo.marca || "",
        modelo: equipo.modelo || "",
        numero_serie: equipo.numero_serie || "",
        tipo_equipo: equipo.tipo_equipo || "",
        procesador: equipo.procesador || "",
        memoria_ram: equipo.memoria_ram || equipo.ram || "",
        tipo_ram: equipo.tipo_ram || "",
        disco: equipo.disco || "",
        tipo_disco: equipo.tipo_disco || "",
        estado: equipo.estado || "operativo",
        ubicacion: equipo.ubicacion || "",
        observaciones: equipo.comentarios || equipo.observaciones || "",
        cliente_id: clienteIdNormalizado,
        sucursal: equipo.sucursal || "",
        departamento: equipo.departamento || "",
        sistema_operativo: equipo.sistema_operativo || "",
        tipo_memoria: equipo.tipo_memoria || "",
        pantalla_pulgadas: equipo.pantalla_pulgadas || "",
        conectividad: equipo.conectividad || "",
        puertos_lan: equipo.puertos_lan || "",
        protocolo_wifi: equipo.protocolo_wifi || "",
        frecuencia_wifi: equipo.frecuencia_wifi || "",
        velocidad_wan: equipo.velocidad_wan || "",
        tipo_switch: equipo.tipo_switch || "",
        poe: equipo.poe || "",
        firmware_version: equipo.firmware_version || "",
        tipo_impresion: equipo.tipo_impresion || "",
        conexion_impresora: equipo.conexion_impresora || "",
        resolucion_dpi: equipo.resolucion_dpi || "",
        formato_papel: equipo.formato_papel || "",
        capacidad_va: equipo.capacidad_va || "",
        potencia_w: equipo.potencia_w || "",
        autonomia_min: equipo.autonomia_min || "",
        tipo_bateria_ups: equipo.tipo_bateria_ups || ""
      });
      
      setClienteSeleccionado(clienteEncontrado || null);
      
      // Cargar departamentos del cliente
      if (clienteIdNormalizado) {
        const token = localStorage.getItem("token");
        http.get(`/clientes/${clienteIdNormalizado}/departamentos`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(deptRes => {
          setDepartamentosDisponibles(Array.isArray(deptRes.data) ? deptRes.data : []);
        }).catch(() => {
          setDepartamentosDisponibles([]);
        });
      }
    } else {
      resetForm();
    }
  }, [equipo, clientes]);

  async function cargarClientes() {
    try {
      const token = localStorage.getItem("token");
      const res = await http.get("/clientes", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const clientesData = Array.isArray(res.data) ? res.data : [];
      
      // Ordenar clientes alfabéticamente
      clientesData.sort((a, b) => (a.nombre || "").toLowerCase().localeCompare((b.nombre || "").toLowerCase()));

      // Normalizar clientes con primaryId y lookupIds
      const clientesNormalizados = clientesData.map(c => {
        const lookupIds = [
          c.id, 
          c._id, 
          c.mongo_id,
          c.primaryId,
          ...(c.lookupIds || [])
        ].filter(Boolean).map(String);
        
        return {
          ...c,
          primaryId: c.id || c._id || c.primaryId || lookupIds[0],
          lookupIds: [...new Set(lookupIds)]
        };
      });
      
      console.log("Clientes cargados:", clientesNormalizados);
      setClientes(clientesNormalizados);
    } catch (err) {
      console.error("Error cargando clientes", err);
    }
  }

  function resetForm() {
    setForm({
      nombre: "",
      marca: "",
      modelo: "",
      numero_serie: "",
      tipo_equipo: "",
      procesador: "",
      memoria_ram: "",
      tipo_ram: "",
      disco: "",
      tipo_disco: "",
      tipo_memoria: "",
      sistema_operativo: "",
      pantalla_pulgadas: "",
      conectividad: "",
      puertos_lan: "",
      protocolo_wifi: "",
      frecuencia_wifi: "",
      velocidad_wan: "",
      tipo_switch: "",
      poe: "",
      firmware_version: "",
      tipo_impresion: "",
      conexion_impresora: "",
      resolucion_dpi: "",
      formato_papel: "",
      capacidad_va: "",
      potencia_w: "",
      autonomia_min: "",
      tipo_bateria_ups: "",
      estado: "operativo",
      ubicacion: "",
      observaciones: "",
      cliente_id: "",
      sucursal: "",
      departamento: ""
    });
  }

  const handleClienteChange = async (clienteId) => {
    const cliente = clientes.find(c => c.primaryId === clienteId || c.id === clienteId);
    setClienteSeleccionado(cliente || null);
    setDepartamentosDisponibles([]);
    setForm(prev => ({
      ...prev,
      cliente_id: clienteId,
      sucursal: "",
      departamento: ""
    }));
    
    if (clienteId) {
      try {
        const token = localStorage.getItem("token");
        const deptRes = await http.get(`/clientes/${clienteId}/departamentos`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDepartamentosDisponibles(Array.isArray(deptRes.data) ? deptRes.data : []);
      } catch (e) {
        setDepartamentosDisponibles([]);
      }
    }
  };

  async function guardarEquipo() {
    // Validar campos requeridos
    const camposRequeridos = {
      marca: "Marca",
      modelo: "Modelo",
      tipo_equipo: "Tipo de equipo",
      cliente_id: "Cliente"
    };

    for (const [campo, nombre] of Object.entries(camposRequeridos)) {
      if (!form[campo] || (typeof form[campo] === 'string' && !form[campo].trim())) {
        alert(`El campo ${nombre} es requerido`);
        return;
      }
    }

    setGuardando(true);

    try {
      // Preparar datos para enviar
      const dataToSend = {
        ...form,
        nombre: form.nombre.trim(),
        marca: form.marca.trim(),
        modelo: form.modelo.trim(),
        tipo_equipo: form.tipo_equipo.trim(),
        numero_serie: form.numero_serie.trim(),
        estado: form.estado || "operativo",
        ubicacion: form.ubicacion.trim(),
        // Mantener RAM y disco como texto para preservar especificaciones completas
        // Convertir a String primero para evitar error .trim() en valores numéricos
        memoria_ram: form.memoria_ram != null ? String(form.memoria_ram).trim() : null,
        tipo_ram: form.tipo_ram != null ? String(form.tipo_ram).trim() : null,
        disco: form.disco != null ? String(form.disco).trim() : null,
        tipo_disco: form.tipo_disco != null ? String(form.tipo_disco).trim() : null,
        // Asegurarnos de que el cliente_id sea el correcto
        cliente_id: form.cliente_id,
        // Asegurar que observaciones se envíe (manejar strings vacíos o null)
        observaciones: form.observaciones ? form.observaciones.trim() : "",
        departamento: form.departamento || ""
      };

      console.log("Enviando datos:", dataToSend);
      console.log("ID del equipo:", equipo?._id);

      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No hay token de autorización");
      }

      // Usar el ID del equipo que viene del backend
      const equipoId = equipo?._id || equipo?.id;
      console.log("ID del equipo a actualizar:", equipoId);
      
      if (equipo && !equipoId) {
        throw new Error("ID de equipo no válido");
      }

      // Preparar los headers
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      console.log("Intentando guardar equipo con los siguientes datos:", {
        headers,
        data: dataToSend,
        endpoint: equipo ? `/equipos/${equipoId}` : '/equipos/create'
      });

      // Para crear un equipo, usamos /equipos/create
      // Para actualizar un equipo, usamos /equipos/{id}
      const res = equipo
        ? await http.put(
            `/equipos/${equipoId}`, 
            dataToSend, 
            { headers }
          )
        : await http.post(
            "/equipos/create", 
            dataToSend, 
            { headers }
          );

      if (!res.data) {
        throw new Error("No se recibió respuesta del servidor");
      }

      console.log("Respuesta del servidor:", res.data);
      alert("✅ Equipo guardado correctamente");
      onGuardado();
    } catch (err) {
      console.error("Error guardando equipo", err);
      console.log("Detalles de la solicitud:", {
        url: err.config?.url,
        method: err.config?.method,
        headers: err.config?.headers,
        data: err.config?.data
      });
      console.log("Detalles de la respuesta:", {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data
      });
      
      let mensaje = "Error al guardar equipo";
      
      if (err.response) {
        // Error de respuesta del servidor
        if (err.response.status === 405) {
          mensaje = "Método no permitido. Por favor, actualiza la página y vuelve a intentarlo.";
          // Intentar determinar qué métodos están permitidos
          const allowedMethods = err.response.headers?.['allow'];
          if (allowedMethods) {
            console.log("Métodos permitidos:", allowedMethods);
          }
        } else {
          mensaje = err.response.data?.detail || "Error en la respuesta del servidor";
        }
      } else if (err.request) {
        // Error de red
        mensaje = "No se pudo conectar con el servidor. Verifica tu conexión.";
      } else {
        // Error de código
        mensaje = err.message;
      }
      
      alert(`❌ ${mensaje}`);
    } finally {
      setGuardando(false);
    }
  }

  const SectionHeader = ({ id, titulo, icono: Icono, isOpen }) => (
    <button
      type="button"
      onClick={() => setSeccionAbierta(isOpen ? null : id)}
      className={`w-full flex items-center justify-between p-4 rounded-lg border transition-all duration-200 ${
        isOpen 
          ? 'bg-emerald-50 border-emerald-200 text-emerald-700' 
          : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
      }`}
    >
      <div className="flex items-center gap-3">
        <Icono className="w-5 h-5" />
        <span className="font-medium text-left">{titulo}</span>
      </div>
      {isOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
    </button>
  );

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-end z-50">
      {/* Modal que ocupa toda la pantalla en móvil */}
      <div className="bg-white w-full h-full overflow-y-auto">
        {/* Header fijo */}
        <div className="sticky top-0 bg-white border-b z-10 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Monitor className="w-6 h-6 text-emerald-600" />
              <h2 className="text-lg font-semibold text-gray-800">
                {equipo ? "Editar Equipo" : "Nuevo Equipo"}
              </h2>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={guardarEquipo}
                disabled={guardando}
                className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {guardando ? "Guardando..." : "Guardar"}
              </button>
              
              <button
                type="button"
                onClick={onCancelar}
                className="flex items-center justify-center w-10 h-10 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Contenido del formulario */}
        <div className="p-4 space-y-4 pb-20">
          
          {/* Sección: Información Básica */}
          <div>
            <SectionHeader 
              id="basicos" 
              titulo="Información Básica" 
              icono={Monitor}
              isOpen={seccionAbierta === 'basicos'} 
            />
            
            {seccionAbierta === 'basicos' && (
              <div className="mt-4 space-y-4 p-4 bg-emerald-50 rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre del Equipo *
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Ej: PC-Administracion-01"
                    value={form.nombre}
                    onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Equipo *
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
                    value={form.tipo_equipo}
                    onChange={(e) => setForm({ ...form, tipo_equipo: e.target.value })}
                  >
                    <option value="">Seleccionar tipo</option>
                    {TIPOS_EQUIPO.map(tipo => (
                      <option key={tipo} value={tipo}>{tipo}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Marca
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Ej: Dell, HP, Lenovo"
                    value={form.marca}
                    onChange={(e) => setForm({ ...form, marca: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Modelo
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Ej: Latitude 7420"
                    value={form.modelo}
                    onChange={(e) => setForm({ ...form, modelo: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Número de Serie
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Serial número del equipo"
                    value={form.numero_serie}
                    onChange={(e) => setForm({ ...form, numero_serie: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estado
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
                    value={form.estado}
                    onChange={(e) => setForm({ ...form, estado: e.target.value })}
                  >
                    <option value="operativo">Operativo</option>
                    <option value="mantenimiento">En Mantenimiento</option>
                    <option value="averiado">Averiado</option>
                    <option value="baja">Dado de Baja</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Sección: Especificaciones Técnicas */}
          <div>
            <SectionHeader 
              id="especificaciones" 
              titulo="Especificaciones Técnicas" 
              icono={Cpu}
              isOpen={seccionAbierta === 'especificaciones'} 
            />
            
            {seccionAbierta === 'especificaciones' && (
              <div className="mt-4 space-y-4">
                {!form.tipo_equipo ? (
                  <div className="p-4 bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg text-center">
                    <Monitor className="w-7 h-7 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Seleccione primero el tipo de equipo en "Información Básica"</p>
                  </div>
                ) : (
                  <>
                    {/* CÓMPUTO */}
                    {getCategoriaEquipo(form.tipo_equipo) === 'computador' && (
                      <div className="p-4 bg-purple-50 rounded-lg space-y-4">
                        <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Especificaciones de Cómputo — {form.tipo_equipo}</p>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{form.tipo_equipo === 'Tablet' ? 'Procesador / SoC' : 'Procesador'}</label>
                          <input type="text" className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-purple-500"
                            placeholder={form.tipo_equipo === 'Tablet' ? 'Ej: Apple A16, Snapdragon 8 Gen 2' : 'Ej: Intel Core i7-10700K'}
                            value={form.procesador} onChange={(e) => setForm({ ...form, procesador: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Memoria RAM</label>
                          <input type="text" className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-purple-500"
                            placeholder="Ej: 8GB, 16GB" value={form.memoria_ram} onChange={(e) => setForm({ ...form, memoria_ram: e.target.value })} />
                        </div>
                        {form.tipo_equipo !== 'Tablet' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de RAM</label>
                            <select className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                              value={form.tipo_ram} onChange={(e) => setForm({ ...form, tipo_ram: e.target.value })}>
                              <option value="">Seleccionar</option>
                              <option value="DDR3">DDR3</option>
                              <option value="DDR4">DDR4</option>
                              <option value="DDR5">DDR5</option>
                              <option value="SODIMM DDR3">SODIMM DDR3</option>
                              <option value="SODIMM DDR4">SODIMM DDR4</option>
                              <option value="SODIMM DDR5">SODIMM DDR5</option>
                              <option value="LPDDR4">LPDDR4</option>
                              <option value="LPDDR5">LPDDR5</option>
                            </select>
                          </div>
                        )}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{form.tipo_equipo === 'Tablet' ? 'Almacenamiento Interno' : 'Almacenamiento'}</label>
                          <input type="text" className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-purple-500"
                            placeholder="Ej: 256GB, 1TB" value={form.disco} onChange={(e) => setForm({ ...form, disco: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Almacenamiento</label>
                          <input type="text" className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-purple-500"
                            placeholder={form.tipo_equipo === 'Tablet' ? 'Ej: eMMC, UFS, NVMe' : 'Ej: SSD, HDD, NVMe'}
                            value={form.tipo_disco} onChange={(e) => setForm({ ...form, tipo_disco: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Sistema Operativo</label>
                          <input type="text" className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-purple-500"
                            placeholder={form.tipo_equipo === 'Tablet' ? 'Ej: Android 14, iPadOS 17' : 'Ej: Windows 11 Pro'}
                            value={form.sistema_operativo} onChange={(e) => setForm({ ...form, sistema_operativo: e.target.value })} />
                        </div>
                        {['Notebook', 'Tablet'].includes(form.tipo_equipo) && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Pantalla (pulgadas)</label>
                            <input type="text" className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-purple-500"
                              placeholder="Ej: 10.2, 13.3, 15.6" value={form.pantalla_pulgadas} onChange={(e) => setForm({ ...form, pantalla_pulgadas: e.target.value })} />
                          </div>
                        )}
                        {form.tipo_equipo === 'Tablet' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Conectividad</label>
                            <select className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                              value={form.conectividad} onChange={(e) => setForm({ ...form, conectividad: e.target.value })}>
                              <option value="">Seleccionar</option>
                              <option value="WiFi">WiFi</option>
                              <option value="WiFi + 4G">WiFi + 4G (LTE)</option>
                              <option value="WiFi + 5G">WiFi + 5G</option>
                              <option value="Solo Cellular">Solo Cellular</option>
                            </select>
                          </div>
                        )}
                      </div>
                    )}

                    {/* RED */}
                    {getCategoriaEquipo(form.tipo_equipo) === 'red' && (
                      <div className="p-4 bg-emerald-50 rounded-lg space-y-4">
                        <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Especificaciones de Red — {form.tipo_equipo}</p>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Puertos LAN</label>
                          <input type="text" className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            placeholder="Ej: 4x GbE" value={form.puertos_lan} onChange={(e) => setForm({ ...form, puertos_lan: e.target.value })} />
                        </div>
                        {['Router', 'AP'].includes(form.tipo_equipo) && (<>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Protocolo WiFi</label>
                            <select className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
                              value={form.protocolo_wifi} onChange={(e) => setForm({ ...form, protocolo_wifi: e.target.value })}>
                              <option value="">Sin WiFi</option>
                              <option value="802.11n (Wi-Fi 4)">802.11n — Wi-Fi 4</option>
                              <option value="802.11ac (Wi-Fi 5)">802.11ac — Wi-Fi 5</option>
                              <option value="802.11ax (Wi-Fi 6)">802.11ax — Wi-Fi 6</option>
                              <option value="802.11ax (Wi-Fi 6E)">802.11ax — Wi-Fi 6E</option>
                              <option value="802.11be (Wi-Fi 7)">802.11be — Wi-Fi 7</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Banda / Frecuencia</label>
                            <select className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
                              value={form.frecuencia_wifi} onChange={(e) => setForm({ ...form, frecuencia_wifi: e.target.value })}>
                              <option value="">Seleccionar</option>
                              <option value="2.4 GHz">2.4 GHz</option>
                              <option value="5 GHz">5 GHz</option>
                              <option value="Dual Band (2.4 + 5 GHz)">Dual Band</option>
                              <option value="Tri Band">Tri Band</option>
                            </select>
                          </div>
                        </>)}
                        {['Router', 'Firewall'].includes(form.tipo_equipo) && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Velocidad WAN</label>
                            <input type="text" className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500"
                              placeholder="Ej: 1 Gbps" value={form.velocidad_wan} onChange={(e) => setForm({ ...form, velocidad_wan: e.target.value })} />
                          </div>
                        )}
                        {form.tipo_equipo === 'Switch' && (<>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Switch</label>
                            <select className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
                              value={form.tipo_switch} onChange={(e) => setForm({ ...form, tipo_switch: e.target.value })}>
                              <option value="">Seleccionar</option>
                              <option value="Gestionable">Gestionable (Managed)</option>
                              <option value="No gestionable">No gestionable (Unmanaged)</option>
                              <option value="Smart">Smart</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">PoE</label>
                            <select className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
                              value={form.poe} onChange={(e) => setForm({ ...form, poe: e.target.value })}>
                              <option value="">No especificado</option>
                              <option value="Sí">Sí — PoE</option>
                              <option value="No">No</option>
                            </select>
                          </div>
                        </>)}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Versión Firmware</label>
                          <input type="text" className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            placeholder="Ej: v2.1.4" value={form.firmware_version} onChange={(e) => setForm({ ...form, firmware_version: e.target.value })} />
                        </div>
                      </div>
                    )}

                    {/* IMPRESORA */}
                    {getCategoriaEquipo(form.tipo_equipo) === 'impresora' && (
                      <div className="p-4 bg-teal-50 rounded-lg space-y-4">
                        <p className="text-xs font-semibold text-teal-700 uppercase tracking-wide">Especificaciones de Impresora</p>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Impresión</label>
                          <select className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                            value={form.tipo_impresion} onChange={(e) => setForm({ ...form, tipo_impresion: e.target.value })}>
                            <option value="">Seleccionar</option>
                            <option value="Láser">Láser</option>
                            <option value="Inkjet">Inkjet</option>
                            <option value="Térmica">Térmica</option>
                            <option value="Matricial">Matricial</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Conexión</label>
                          <select className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                            value={form.conexion_impresora} onChange={(e) => setForm({ ...form, conexion_impresora: e.target.value })}>
                            <option value="">Seleccionar</option>
                            <option value="USB">USB</option>
                            <option value="Red (Ethernet)">Red (Ethernet)</option>
                            <option value="WiFi">WiFi</option>
                            <option value="USB + WiFi">USB + WiFi</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Resolución (DPI)</label>
                          <input type="text" className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-teal-500"
                            placeholder="Ej: 600x600" value={form.resolucion_dpi} onChange={(e) => setForm({ ...form, resolucion_dpi: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Formato de Papel</label>
                          <select className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                            value={form.formato_papel} onChange={(e) => setForm({ ...form, formato_papel: e.target.value })}>
                            <option value="">Seleccionar</option>
                            <option value="A4">A4</option>
                            <option value="A3">A3</option>
                            <option value="A4/A3">A4 y A3</option>
                            <option value="Carta">Carta</option>
                            <option value="Rollo 80mm">Rollo 80mm</option>
                          </select>
                        </div>
                      </div>
                    )}

                    {/* UPS */}
                    {getCategoriaEquipo(form.tipo_equipo) === 'ups' && (
                      <div className="p-4 bg-yellow-50 rounded-lg space-y-4">
                        <p className="text-xs font-semibold text-yellow-700 uppercase tracking-wide">Especificaciones UPS</p>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Capacidad (VA)</label>
                          <input type="text" className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-yellow-500"
                            placeholder="Ej: 600, 1000" value={form.capacidad_va} onChange={(e) => setForm({ ...form, capacidad_va: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Potencia (W)</label>
                          <input type="text" className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-yellow-500"
                            placeholder="Ej: 360, 600" value={form.potencia_w} onChange={(e) => setForm({ ...form, potencia_w: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Autonomía (minutos)</label>
                          <input type="text" className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-yellow-500"
                            placeholder="Ej: 15, 30" value={form.autonomia_min} onChange={(e) => setForm({ ...form, autonomia_min: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Batería</label>
                          <select className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white"
                            value={form.tipo_bateria_ups} onChange={(e) => setForm({ ...form, tipo_bateria_ups: e.target.value })}>
                            <option value="">Seleccionar</option>
                            <option value="VRLA / SLA">VRLA / SLA (Sellada)</option>
                            <option value="Litio-Ion">Litio-Ion</option>
                            <option value="NiMH">NiMH</option>
                          </select>
                        </div>
                      </div>
                    )}

                    {/* GENÉRICO */}
                    {getCategoriaEquipo(form.tipo_equipo) === 'generico' && (
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500 text-center">No hay especificaciones técnicas adicionales para este tipo de equipo.</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Sección: Cliente y Ubicación */}
          <div>
            <SectionHeader 
              id="cliente" 
              titulo="Cliente y Ubicación" 
              icono={User}
              isOpen={seccionAbierta === 'cliente'} 
            />
            
            {seccionAbierta === 'cliente' && (
              <div className="mt-4 space-y-4 p-4 bg-purple-50 rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cliente *
                  </label>
                  <SearchableSelect
                    placeholder="Seleccionar cliente"
                    className="w-full"
                    value={form.cliente_id}
                    options={clientes.map(cliente => ({ value: cliente.primaryId || cliente.id, label: cliente.nombre }))}
                    onChange={(val) => handleClienteChange(val)}
                  />
                </div>

                {clienteSeleccionado?.sucursales?.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sucursal
                    </label>
                    <select
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                      value={form.sucursal}
                      onChange={(e) => setForm({ ...form, sucursal: e.target.value, departamento: "" })}
                    >
                      <option value="">Oficina principal</option>
                      {clienteSeleccionado.sucursales.map((sucursal, index) => {
                        // Manejar tanto strings (formato antiguo) como objetos (formato nuevo)
                        const nombre = typeof sucursal === 'string' ? sucursal : sucursal.nombre;
                        return (
                          <option key={index} value={nombre}>
                            {nombre}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}

                {departamentosDisponibles.length > 0 && (() => {
                  const deptsFiltrados = form.sucursal
                    ? departamentosDisponibles.filter(d => !d.sucursal_nombre || d.sucursal_nombre === form.sucursal)
                    : departamentosDisponibles.filter(d => !d.sucursal_nombre);
                  if (deptsFiltrados.length === 0) return null;
                  return (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <Layers className="w-4 h-4" />
                        Departamento / Área
                      </label>
                      <select
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                        value={form.departamento}
                        onChange={(e) => setForm({ ...form, departamento: e.target.value })}
                      >
                        <option value="">Sin departamento</option>
                        {deptsFiltrados.map((d, i) => (
                          <option key={i} value={d.nombre}>{d.nombre}</option>
                        ))}
                      </select>
                    </div>
                  );
                })()}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Ubicación Física
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Ej: Oficina 2do piso, Sala de reuniones"
                    value={form.ubicacion}
                    onChange={(e) => setForm({ ...form, ubicacion: e.target.value })}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Sección: Observaciones */}
          <div>
            <SectionHeader 
              id="observaciones" 
              titulo="Observaciones y Notas" 
              icono={FileText}
              isOpen={seccionAbierta === 'observaciones'} 
            />
            
            {seccionAbierta === 'observaciones' && (
              <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Observaciones
                  </label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 resize-none"
                    placeholder="Cualquier información adicional sobre el equipo..."
                    rows="4"
                    value={form.observaciones}
                    onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
                  />
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  , document.body);
}